
import { Box, Paper, Typography, Button, SxProps } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export interface InfoCardProps {
    title: string;
    description?: string;
    icon?: ReactNode;
    variant?: 'default' | 'glass' | 'outlined' | 'filled';
    centered?: boolean;
    hoverEffect?: boolean;
    titleSize?: 'small' | 'medium' | 'large';
    titleColor?: string;
    tagline?: string;
    taglineColor?: string;
    cta?: {
        label: string;
        href?: string;
        onClick?: () => void;
        external?: boolean;
    };
    orientation?: 'vertical' | 'horizontal';
    meta?: ReactNode;
    sx?: SxProps;
    padding?: number;
}

export function InfoCard({
    title,
    description,
    tagline,
    taglineColor,
    icon,
    variant = 'default',
    centered = false,
    hoverEffect = true,
    titleSize = 'medium',
    titleColor,
    cta,
    orientation = 'vertical',
    meta,
    sx,
    padding = 4,
}: InfoCardProps) {

    // Base styles
    const baseStyles: SxProps = {
        p: padding,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: centered ? 'center' : 'left',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        ...(hoverEffect && {
            '&:hover': {
                transform: 'translateY(-4px)',
                // Shadow will be added per variant if needed
            },
        }),
    };

    // Variant specific styles
    const variantStyles: Record<string, SxProps> = {
        default: {
            bgcolor: 'background.paper',
            // Default paper elevation usually handles shadow, but we can enforce border if preferred
            // border: '1px solid',
            // borderColor: 'divider', 
            // User style reference: PricingCard uses transparent with border, About "Our Principles" uses border
            border: '1px solid',
            borderColor: 'divider',
        },
        outlined: {
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
        },
        glass: {
            bgcolor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': hoverEffect ? {
                bgcolor: 'rgba(255,255,255,0.08)',
                transform: 'translateY(-4px)',
                borderColor: 'rgba(255,255,255,0.2)',
            } : {},
        },
        filled: {
            bgcolor: 'grey.50',
            border: 'none',
        }
    };

    const getTypographyVariant = (size: 'small' | 'medium' | 'large') => {
        switch (size) {
            case 'small': return 'h6';
            case 'medium': return 'h4';
            case 'large': return 'h3';
            default: return 'h4';
        }
    };

    const getTypographyFontSize = (size: 'small' | 'medium' | 'large') => {
        switch (size) {
            case 'small': return '1.125rem';
            case 'medium': return { xs: '1.25rem', md: '1.5rem' };
            case 'large': return { xs: '1.75rem', md: '2rem' };
            default: return { xs: '1.25rem', md: '1.5rem' };
        }
    };

    return (
        <Paper
            elevation={0}
            sx={[
                baseStyles as any,
                variantStyles[variant],
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {tagline && (
                <Typography
                    variant="body2"
                    sx={{
                        color: taglineColor || (variant === 'glass' ? 'white' : 'text.primary'),
                        fontWeight: 700,
                        mb: 1,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: 0.5
                    }}
                >
                    {tagline}
                </Typography>
            )}

            {orientation === 'horizontal' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: centered ? 'center' : 'flex-start' }}>
                    {icon && (
                        <Box sx={{ mr: 2, display: 'flex', flexShrink: 0 }}>
                            {icon}
                        </Box>
                    )}
                    <Typography
                        variant={getTypographyVariant(titleSize)}
                        component="h3"
                        sx={{
                            fontWeight: 700,
                            fontSize: getTypographyFontSize(titleSize),
                            color: titleColor || (variant === 'glass' ? 'white' : 'text.primary'),
                            lineHeight: 1.2
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            ) : (
                <>
                    {icon && (
                        <Box sx={{ mb: 2, mx: centered ? 'auto' : 0 }}>
                            {icon}
                        </Box>
                    )}
                    <Typography
                        variant={getTypographyVariant(titleSize)}
                        component="h3"
                        sx={{
                            mb: 1,
                            fontWeight: 700,
                            fontSize: getTypographyFontSize(titleSize), // Consistent H4/H5 sizing
                            color: titleColor || (variant === 'glass' ? 'white' : 'text.primary')
                        }}
                    >
                        {title}
                    </Typography>
                </>
            )}

            {meta && (
                <Box sx={{ mb: 2 }}>
                    {meta}
                </Box>
            )}

            {description && (
                <Typography
                    variant="body2"
                    sx={{
                        color: variant === 'glass' ? 'grey.400' : 'text.secondary',
                        flexGrow: 1,
                        lineHeight: 1.6
                    }}
                >
                    {description}
                </Typography>
            )}

            {cta && (
                <Box sx={{ mt: 3 }}>
                    {cta.onClick ? (
                        <Button
                            onClick={cta.onClick}
                            variant={variant === 'glass' ? 'outlined' : 'text'}
                            color={variant === 'glass' ? 'inherit' : 'primary'}
                            endIcon={!cta.external && <ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
                            sx={{
                                ...(variant === 'glass' && { borderColor: 'rgba(255,255,255,0.3)', color: 'white' }),
                                fontWeight: 600,
                                p: 0,
                                minWidth: 'auto',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: 'transparent',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            {cta.label}
                        </Button>
                    ) : (
                        <Button
                            component={cta.external ? 'a' : Link}
                            {...(cta.external ? { href: cta.href, target: '_blank', rel: 'noopener noreferrer' } : { to: cta.href ?? '#' })}
                            variant={variant === 'glass' ? 'outlined' : 'text'}
                            color={variant === 'glass' ? 'inherit' : 'primary'}
                            sx={{
                                ...(variant === 'glass' && { borderColor: 'rgba(255,255,255,0.3)', color: 'white' }),
                                fontWeight: 600
                            }}
                        >
                            {cta.label}
                        </Button>
                    )}
                </Box>
            )}
        </Paper>
    );
}
